import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Brain, TrendingUp, Users, Gift, Lightbulb, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/card';

export const AdminAIInsights = () => {
  const { adminApi } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await adminApi.get('/admin/ai/insights');
      setInsights(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ai-insights">
      <div className="flex items-center gap-3">
        <Brain className="w-8 h-8 text-afriperx-gold" />
        <div>
          <h1 className="font-syne text-2xl font-bold text-gray-900">AI Insights</h1>
          <p className="text-gray-600">Intelligent recommendations and analytics</p>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-gray-500 text-sm">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{insights?.user_count || 0}</p>
        </Card>
        <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-gray-500 text-sm">Total Balance</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">R{insights?.total_balance?.toFixed(2) || '0.00'}</p>
        </Card>
        <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-5 h-5 text-purple-600" />
            <span className="text-gray-500 text-sm">Total Savings</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">R{insights?.total_savings?.toFixed(2) || '0.00'}</p>
        </Card>
      </div>

      {/* AI Recommendations */}
      <Card className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
        <h2 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          AI Recommendations
        </h2>
        <div className="space-y-4">
          {insights?.recommendations?.map((rec, index) => (
            <div 
              key={index}
              className="p-4 bg-gradient-to-r from-afriperx-green/5 to-transparent border-l-2 border-afriperx-gold rounded-r-lg"
            >
              <p className="text-gray-900 font-medium">{rec}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Strategic Insights */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <Gift className="w-8 h-8 text-purple-600 mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">Loyalty Program</h3>
          <p className="text-gray-600 text-sm">
            Users with 2,500+ points show 40% higher engagement. Consider bonus campaigns for tier upgrades.
          </p>
        </Card>
        <Card className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
          <TrendingUp className="w-8 h-8 text-cyan-600 mb-3" />
          <h3 className="text-gray-900 font-medium mb-1">Growth Opportunity</h3>
          <p className="text-gray-600 text-sm">
            Marketplace spending correlates with higher retention. Promote health-based rewards to increase engagement.
          </p>
        </Card>
      </div>
    </div>
  );
};
